<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { SortOption } from '../types';
import Search from './icons/Search.vue';
import X from './icons/X.vue';

const { t } = useI18n();

interface Props {
  allTags: string[];
  selectedTags: string[];
  sortBy: SortOption;
  minScore: number;
  searchQuery: string;
  groupedDots: boolean;
  visibleVoteTypes: ('liked' | 'important' | 'supported')[];
  groupByTag?: boolean;
  displayMode?: 'grid' | 'list';
  compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  groupByTag: false,
  displayMode: 'grid',
  compact: false,
});

const emit = defineEmits({
  'update:selectedTags': (_tags: string[]) => true,
  'update:sortBy': (_sort: SortOption) => true,
  'update:minScore': (_score: number) => true,
  'update:searchQuery': (_query: string) => true,
  'update:groupedDots': (_grouped: boolean) => true,
  'update:groupByTag': (_grouped: boolean) => true,
  'update:displayMode': (_mode: 'grid' | 'list') => true,
  toggleVoteType: (_type: 'liked' | 'important' | 'supported') => true,
  clearFilters: () => true,
});

const sortOptions = computed(() => [
  { value: 'newest' as SortOption, label: t('filters.newest') },
  { value: 'total' as SortOption, label: t('filters.totalScore') },
  { value: 'liked' as SortOption, label: t('filters.mostLiked') },
  { value: 'important' as SortOption, label: t('filters.mostImportant') },
  { value: 'supported' as SortOption, label: t('filters.mostSupported') },
]);

function toggleTag(tag: string) {
  const current = [...props.selectedTags];
  const index = current.indexOf(tag);
  if (index > -1) {
    current.splice(index, 1);
  } else {
    current.push(tag);
  }
  emit('update:selectedTags', current);
}

function clearFilters() {
  emit('update:selectedTags', []);
  emit('update:minScore', 0);
  emit('update:searchQuery', '');
  emit('clearFilters');
}
</script>

<template>
  <!-- Compact layout for wide screens -->
  <div v-if="compact" class="space-y-4">
    <!-- Top row: Search, Sort, Score in a grid -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <!-- Search -->
      <div>
        <label class="text-xs font-medium mb-1 block">{{ $t('filters.search') }}</label>
        <div class="relative">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            :value="searchQuery"
            @input="emit('update:searchQuery', ($event.target as HTMLInputElement).value)"
            :placeholder="$t('filters.searchPlaceholder')"
            class="w-full pl-9 pr-8 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-(--color-primary)"
          />
          <button
            v-if="searchQuery"
            @click="emit('update:searchQuery', '')"
            class="absolute right-2 top-1/2 -translate-y-1/2"
          >
            <X class="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </button>
        </div>
      </div>

      <!-- Sort -->
      <div>
        <label class="text-xs font-medium mb-1 block">{{ $t('filters.sortBy') }}</label>
        <select
          :value="sortBy"
          @change="emit('update:sortBy', ($event.target as HTMLSelectElement).value as SortOption)"
          class="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-(--color-primary)"
        >
          <option v-for="option in sortOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <!-- Score filter -->
      <div>
        <label class="text-xs font-medium mb-1 block">
          {{ $t('filters.minScore') }}: <span class="text-(--color-primary)">{{ minScore }}</span>
        </label>
        <input
          type="range"
          :value="minScore"
          @input="emit('update:minScore', parseInt(($event.target as HTMLInputElement).value))"
          min="0"
          max="30"
          class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-(--color-primary) mt-2"
        />
        <div class="flex justify-between text-xs text-gray-500 mt-1">
          <span>0</span>
          <span>30</span>
        </div>
      </div>
    </div>

    <!-- Second row: Tags, Vote Types, Display in one row on widescreen -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 pt-2 border-t border-gray-200">
      <!-- Tags -->
      <div v-if="allTags.length > 0">
        <label class="text-xs font-medium mb-1 block">
          {{ $t('idea.tags') }}
          <span v-if="selectedTags.length > 0" class="text-gray-500">
            ({{ selectedTags.length }})
          </span>
        </label>
        <div class="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pr-1 scrollbar-white">
          <button
            v-for="tag in allTags"
            :key="tag"
            @click="toggleTag(tag)"
            :class="[
              'px-2 py-0.5 text-xs rounded-full transition-all duration-200',
              selectedTags.includes(tag)
                ? 'bg-(--color-primary) text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            ]"
          >
            {{ tag }}
          </button>
        </div>
      </div>

      <!-- Vote Types -->
      <div>
        <label class="text-xs font-medium mb-1 block">{{ $t('filters.voteTypes') }}</label>
        <div class="flex flex-wrap gap-2">
          <button
            @click="emit('toggleVoteType', 'liked')"
            class="flex items-center gap-1.5 text-left hover:bg-black/5 rounded px-2 py-1 transition-colors text-sm"
            :class="{ 'opacity-40': !visibleVoteTypes.includes('liked') }"
          >
            <div class="dot dot-liked w-2.5 h-2.5"></div>
            <span
              :class="{ 'line-through': !visibleVoteTypes.includes('liked') }"
              class="text-xs"
              >{{ $t('votes.liked') }}</span
            >
          </button>
          <button
            @click="emit('toggleVoteType', 'important')"
            class="flex items-center gap-1.5 text-left hover:bg-black/5 rounded px-2 py-1 transition-colors text-sm"
            :class="{ 'opacity-40': !visibleVoteTypes.includes('important') }"
          >
            <div class="dot dot-important w-2.5 h-2.5"></div>
            <span
              :class="{ 'line-through': !visibleVoteTypes.includes('important') }"
              class="text-xs"
              >{{ $t('votes.important') }}</span
            >
          </button>
          <button
            @click="emit('toggleVoteType', 'supported')"
            class="flex items-center gap-1.5 text-left hover:bg-black/5 rounded px-2 py-1 transition-colors text-sm"
            :class="{ 'opacity-40': !visibleVoteTypes.includes('supported') }"
          >
            <div class="dot dot-supported w-2.5 h-2.5"></div>
            <span
              :class="{ 'line-through': !visibleVoteTypes.includes('supported') }"
              class="text-xs"
              >{{ $t('votes.supported') }}</span
            >
          </button>
        </div>
      </div>

      <!-- Display Options -->
      <div>
        <label class="text-xs font-medium mb-1 block">{{ $t('filters.display') }}</label>
        <div class="flex flex-wrap gap-x-4 gap-y-2">
          <div class="flex items-center gap-2">
            <input
              type="checkbox"
              :checked="displayMode === 'list'"
              @change="
                emit(
                  'update:displayMode',
                  ($event.target as HTMLInputElement).checked ? 'list' : 'grid'
                )
              "
              id="display-mode-compact"
              class="w-4 h-4 rounded border-gray-300 text-(--color-primary) focus:ring-(--color-primary)"
            />
            <label for="display-mode-compact" class="text-sm cursor-pointer select-none">
              {{ $t('filters.listView') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              type="checkbox"
              :checked="groupedDots"
              @change="emit('update:groupedDots', ($event.target as HTMLInputElement).checked)"
              id="grouped-dots-compact"
              class="w-4 h-4 rounded border-gray-300 text-(--color-primary) focus:ring-(--color-primary)"
            />
            <label for="grouped-dots-compact" class="text-sm cursor-pointer select-none">
              {{ $t('filters.groupedDots') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              type="checkbox"
              :checked="groupByTag"
              @change="emit('update:groupByTag', ($event.target as HTMLInputElement).checked)"
              id="group-by-tag-compact"
              class="w-4 h-4 rounded border-gray-300 text-(--color-primary) focus:ring-(--color-primary)"
            />
            <label for="group-by-tag-compact" class="text-sm cursor-pointer select-none">
              {{ $t('filters.groupByTag') }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Standard layout for mobile/narrow screens -->
  <div v-else class="space-y-6">
    <!-- Search -->
    <div>
      <label class="text-sm font-medium mb-1.5 block">Search</label>
      <div class="relative">
        <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          :value="searchQuery"
          @input="emit('update:searchQuery', ($event.target as HTMLInputElement).value)"
          placeholder="Search ideas..."
          class="w-full pl-9 pr-9 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-(--color-primary)"
        />
        <button
          v-if="searchQuery"
          @click="emit('update:searchQuery', '')"
          class="absolute right-3 top-1/2 -translate-y-1/2"
        >
          <X class="w-4 h-4 text-gray-400 hover:text-gray-600" />
        </button>
      </div>
    </div>

    <!-- Sort -->
    <div>
      <label class="text-sm font-medium mb-1.5 block">Sort by</label>
      <select
        :value="sortBy"
        @change="emit('update:sortBy', ($event.target as HTMLSelectElement).value as SortOption)"
        class="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-(--color-primary)"
      >
        <option v-for="option in sortOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
    </div>

    <!-- Score filter -->
    <div>
      <label class="text-sm font-medium mb-1.5 block">
        Min Score: <span class="text-(--color-primary)">{{ minScore }}</span>
      </label>
      <input
        type="range"
        :value="minScore"
        @input="emit('update:minScore', parseInt(($event.target as HTMLInputElement).value))"
        min="0"
        max="30"
        class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-(--color-primary)"
      />
      <div class="flex justify-between text-xs text-gray-500 mt-1">
        <span>0</span>
        <span>30</span>
      </div>
    </div>

    <!-- Tags -->
    <div v-if="allTags.length > 0">
      <label class="text-sm font-medium mb-1.5 block">
        Tags
        <span v-if="selectedTags.length > 0" class="text-xs text-gray-500 ml-1">
          ({{ selectedTags.length }} selected)
        </span>
      </label>
      <div class="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto pr-1">
        <button
          v-for="tag in allTags"
          :key="tag"
          @click="toggleTag(tag)"
          :class="[
            'px-2.5 py-1 text-xs rounded-full transition-all duration-200 capitalize',
            selectedTags.includes(tag)
              ? 'bg-(--color-primary) text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          ]"
        >
          {{ tag }}
        </button>
      </div>
    </div>

    <!-- Vote Types -->
    <div class="pt-2 border-t border-gray-200">
      <label class="text-sm font-medium mb-2 block">{{ $t('filters.voteTypes') }}</label>
      <div class="space-y-2">
        <button
          @click="emit('toggleVoteType', 'liked')"
          class="flex items-center gap-2 w-full text-left hover:bg-black/5 rounded px-2 py-1.5 transition-colors text-sm"
          :class="{ 'opacity-40': !visibleVoteTypes.includes('liked') }"
        >
          <div class="dot dot-liked"></div>
          <span :class="{ 'line-through': !visibleVoteTypes.includes('liked') }">{{
            $t('votes.liked')
          }}</span>
        </button>
        <button
          @click="emit('toggleVoteType', 'important')"
          class="flex items-center gap-2 w-full text-left hover:bg-black/5 rounded px-2 py-1.5 transition-colors text-sm"
          :class="{ 'opacity-40': !visibleVoteTypes.includes('important') }"
        >
          <div class="dot dot-important"></div>
          <span :class="{ 'line-through': !visibleVoteTypes.includes('important') }">{{
            $t('votes.important')
          }}</span>
        </button>
        <button
          @click="emit('toggleVoteType', 'supported')"
          class="flex items-center gap-2 w-full text-left hover:bg-black/5 rounded px-2 py-1.5 transition-colors text-sm"
          :class="{ 'opacity-40': !visibleVoteTypes.includes('supported') }"
        >
          <div class="dot dot-supported"></div>
          <span :class="{ 'line-through': !visibleVoteTypes.includes('supported') }">{{
            $t('votes.supported')
          }}</span>
        </button>
      </div>
    </div>

    <!-- Display Options -->
    <div class="pt-2 border-t border-gray-200">
      <label class="text-sm font-medium mb-2 block">{{ $t('filters.display') }}</label>
      <div class="space-y-2">
        <div class="flex items-center gap-2">
          <input
            type="checkbox"
            :checked="displayMode === 'list'"
            @change="
              emit(
                'update:displayMode',
                ($event.target as HTMLInputElement).checked ? 'list' : 'grid'
              )
            "
            id="display-mode"
            class="w-4 h-4 rounded border-gray-300 text-(--color-primary) focus:ring-(--color-primary)"
          />
          <label for="display-mode" class="text-sm cursor-pointer select-none">{{
            $t('filters.listView')
          }}</label>
        </div>
        <div class="flex items-center gap-2">
          <input
            type="checkbox"
            :checked="groupedDots"
            @change="emit('update:groupedDots', ($event.target as HTMLInputElement).checked)"
            id="grouped-dots"
            class="w-4 h-4 rounded border-gray-300 text-(--color-primary) focus:ring-(--color-primary)"
          />
          <label for="grouped-dots" class="text-sm cursor-pointer select-none">
            {{ $t('filters.groupedDots') }}
          </label>
        </div>
        <div class="flex items-center gap-2">
          <input
            type="checkbox"
            :checked="groupByTag"
            @change="emit('update:groupByTag', ($event.target as HTMLInputElement).checked)"
            id="group-by-tag"
            class="w-4 h-4 rounded border-gray-300 text-(--color-primary) focus:ring-(--color-primary)"
          />
          <label for="group-by-tag" class="text-sm cursor-pointer select-none">
            {{ $t('filters.groupByTag') }}
          </label>
        </div>
      </div>
    </div>

    <!-- Clear Filters -->
    <div
      v-if="selectedTags.length > 0 || minScore > 0 || searchQuery"
      class="pt-4 border-t border-gray-200"
    >
      <button
        @click="clearFilters"
        class="w-full py-2 text-sm text-(--color-destructive) hover:bg-red-50 rounded-md transition-colors"
      >
        Clear All Filters
      </button>
    </div>
  </div>
</template>
